/**
 * Typed fetch wrapper for the NestJS API.
 *
 * Responsibilities: base URL from env, Bearer-token injection (from auth-token),
 * JSON headers, a request timeout, and normalized errors. HTTP failures throw
 * `ApiException`; unreachable/timed-out requests throw `NetworkError`.
 * Tokens are never read from web storage.
 */

import { ApiException, NetworkError, type ApiErrorBody } from "./api-errors";
import { getAccessToken, getRefreshToken } from "./auth-token";
import { refreshSession } from "./auth-refresh";
import { BASE_URL } from "./api-config";

// Re-exported so existing `import { BASE_URL } from "@/lib/api-client"` keep working.
export { BASE_URL };
const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Requests to these paths must NEVER trigger a token refresh: the login and
 * refresh endpoints authenticate with credentials / the refresh token itself,
 * so a 401 there is terminal (wrong password, expired refresh token) — retrying
 * via refresh would loop.
 */
function isAuthEndpoint(path: string): boolean {
  return path.startsWith("/auth/login") || path.startsWith("/auth/refresh");
}

/** Standard list envelope used by paginated endpoints (docs/api.md §1). */
export interface PaginatedResponse<T> {
  data: T[];
  meta: { page: number; limit: number; total: number };
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;
type JsonBody = Record<string, unknown> | unknown[];

interface RequestOptions {
  params?: QueryParams;
  body?: JsonBody;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Internal flag: set when a request is being replayed after a successful token
   * refresh. Guarantees a request is retried at most ONCE, so a persistently
   * failing 401 can never loop. Callers never set this.
   */
  _retryAfterRefresh?: boolean;
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${BASE_URL}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    qs.append(key, String(value));
  }
  const query = qs.toString();
  return query ? `${url}?${query}` : url;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const token = getAccessToken();
  const hasBody = opts.body !== undefined;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.params), {
      method,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(hasBody ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: hasBody ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    // fetch rejects on network failure or on abort (our timeout).
    throw new NetworkError(controller.signal.aborted ? "Délai dépassé : API injoignable" : "API injoignable");
  } finally {
    clearTimeout(timer);
  }

  // Access token expired → transparently refresh once and replay the request.
  // Only genuine 401s on non-auth endpoints qualify; 403 (forbidden) and every
  // other status fall straight through. `_retryAfterRefresh` caps this at one
  // attempt so a persistent 401 can never loop.
  if (
    res.status === 401 &&
    !opts._retryAfterRefresh &&
    !isAuthEndpoint(path) &&
    getRefreshToken() !== null
  ) {
    // `refreshSession` dedups concurrent 401s into one /auth/refresh call. It
    // returns a new token when refresh succeeds, `null` when the refresh token
    // is rejected (session truly over), or throws NetworkError when the server
    // is unreachable (propagated as-is → the global handler won't log out).
    const newToken = await refreshSession();
    if (newToken) {
      return request<T>(method, path, { ...opts, _retryAfterRefresh: true });
    }
    // Refresh impossible → fall through and surface the original 401 so the
    // global QueryCache handler ends the session exactly once.
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text().catch(() => "");
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const errBody = (parsed as { error?: ApiErrorBody } | null)?.error;
    throw new ApiException(res.status, errBody ?? { code: "UNKNOWN", message: res.statusText || "Erreur" });
  }
  return parsed as T;
}

export const apiGet = <T>(path: string, params?: QueryParams, opts?: Omit<RequestOptions, "params" | "body">): Promise<T> =>
  request<T>("GET", path, { ...opts, params });

export const apiPost = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("POST", path, { ...opts, body });

export const apiPatch = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("PATCH", path, { ...opts, body });

export const apiPut = <T>(path: string, body?: JsonBody, opts?: Omit<RequestOptions, "body">): Promise<T> =>
  request<T>("PUT", path, { ...opts, body });

export const apiDelete = <T>(path: string, opts?: RequestOptions): Promise<T> =>
  request<T>("DELETE", path, opts ?? {});

// Re-exported for backward compatibility with earlier imports.
export { ApiException } from "./api-errors";
